/**
 * Convert Category dialog (Jul 2026 — P1).
 *
 * Master-admin only. Reuses the Add-RM dialog's field-rendering pattern
 * (generic loop over `rmCategories[target].fields`) so operators see the
 * same shape they see when creating natively — plus dedicated inputs
 * for INP mould, INM L2 fields, REGRIND polymer, and PWD/MB pantone.
 *
 * Renders inline the backend's Layer-1 error (list of missing keys) so
 * the operator fixes in one round-trip rather than trial-and-error.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Loader2 } from "lucide-react";
import { MouldSearchSelect } from "./MouldSearchSelect";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Categories with a dedicated backend adapter — for these, generic
// `description_columns.required` is NOT enforced (adapter owns the
// real required contract via `missingCategorySpecific` below). Keep in
// sync with `ADAPTER_CATEGORY_CODES` in backend/services/rm_convert.py.
const ADAPTER_CATEGORIES = new Set(["INP", "INM", "REGRIND", "MB", "PWD"]);

const AUTO_DERIVED = {
  REGRIND: new Set(["polymer_grade"]),
  INP: new Set(["mould_code", "model_name", "part_name", "per_unit_weight", "unit"]),
};

function formatLabel(k) {
  return String(k || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

// Feb 2026 — the production INVALID_MOULD failure surfaced only as
// "Code: INVALID_MOULD" with no explanation, which is why the root
// cause took a code read to diagnose. Map the resolver's error codes
// to operator-facing titles + hints so the dialog is self-explanatory.
function friendlyErrorTitle(err) {
  if (!err) return "Conversion failed";
  switch (err.code) {
    case "MOULD_NOT_FOUND":
      return "That mould doesn't exist";
    case "MOULD_AMBIGUOUS_MATCH":
      return "That mould code matches more than one mould";
    case "MOULD_INELIGIBLE_INACTIVE":
      return "That mould is INACTIVE and can't be used";
    default:
      return err.message || "Conversion failed";
  }
}

function friendlyErrorHint(err) {
  if (!err) return null;
  switch (err.code) {
    case "MOULD_NOT_FOUND":
      return "Pick a mould from the search list above. If you can't find it, add it in Tech Ops → Moulds first.";
    case "MOULD_AMBIGUOUS_MATCH":
      return "De-duplicate the moulds in Tech Ops → Moulds, then try again. The server saw: " + (err.message || "");
    case "MOULD_INELIGIBLE_INACTIVE":
      return "Re-activate the mould in Tech Ops → Moulds, then reopen this dialog.";
    default:
      return err.code ? err.message : null;
  }
}

export default function ConvertCategoryDialog({ rm, rmCategories, open, onClose, onSuccess }) {
  const [target, setTarget] = useState("");
  const [categoryData, setCategoryData] = useState({});
  const [topFields, setTopFields] = useState({});
  // Feb 2026 — Convert → INP now uses the shared MouldSearchSelect.
  // We hold the whole picked mould object here (not just the id) so
  // the summary card renders exactly as it does in Add-INP. The
  // submit still ships `top_fields.mould_id = selectedMould.id`.
  const [selectedMould, setSelectedMould] = useState(null);
  const [convertAsL2, setConvertAsL2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  // Reset when re-opened.
  useEffect(() => {
    if (open) {
      setTarget("");
      setCategoryData({});
      setTopFields({});
      setSelectedMould(null);
      setConvertAsL2(false);
      setServerError(null);
    }
  }, [open, rm?.rm_id]);

  const targetCfg = target && rmCategories?.[target] ? rmCategories[target] : null;
  const targetFields = targetCfg?.fields || [];
  const requiredKeys = useMemo(() => {
    if (!targetCfg) return [];
    // Jul 2026 bug fix — adapter categories own their own required contract
    // (enforced by `missingCategorySpecific` below and by the backend
    // adapter). Some seeded `description_columns` still carry stale
    // `required=true` flags on keys the adapter derives (e.g. INM's
    // `model_name` / `part_name`), which used to strand the Convert button.
    if (ADAPTER_CATEGORIES.has(target)) return [];
    const cols = targetCfg.description_columns || [];
    const auto = AUTO_DERIVED[target] || new Set();
    return cols
      .filter(c => c.required && !auto.has(c.key))
      .map(c => c.key);
  }, [targetCfg, target]);

  // Confirm-enabled predicate — mirrors backend Layer 1.
  const missingLayer1 = requiredKeys.filter(k => {
    const v = categoryData[k];
    return v === undefined || v === null || String(v).trim() === "";
  });

  // Category-specific top-field requirements (client mirror of adapter
  // validation — backend still enforces).
  const missingCategorySpecific = useMemo(() => {
    if (!target) return [];
    if (target === "INP") {
      const miss = [];
      // Feb 2026 — the picker is the ONLY submit path. A typed
      // mould code cannot reach the server; the operator must
      // pick a row (which stamps `topFields.mould_id = mould.id`).
      if (!topFields.mould_id || !String(topFields.mould_id).trim()) miss.push("mould_id");
      if (!categoryData.pantone_code || !String(categoryData.pantone_code).trim()) miss.push("pantone_code");
      return miss;
    }
    if (target === "INM" && convertAsL2) {
      const miss = [];
      if (!categoryData.pantone_code || !String(categoryData.pantone_code).trim()) miss.push("pantone_code");
      if (!topFields.parent_rm_id || !String(topFields.parent_rm_id).trim()) miss.push("parent_rm_id");
      if (!topFields.secondary_l1_rm_id || !String(topFields.secondary_l1_rm_id).trim()) miss.push("secondary_l1_rm_id");
      const pq = parseFloat(topFields.powder_qty_grams);
      if (!(pq > 0)) miss.push("powder_qty_grams");
      return miss;
    }
    if (target === "INM") {
      // Non-L2 INM conversion — just needs a pantone (adapter mints/finds
      // the colourant). No other Layer-1 keys are required by the adapter.
      const miss = [];
      if (!categoryData.pantone_code || !String(categoryData.pantone_code).trim()) miss.push("pantone_code");
      return miss;
    }
    if (target === "MB") {
      // MB adapter requires mb_code (adapter-owned) + pantone.
      const miss = [];
      if (!categoryData.mb_code || !String(categoryData.mb_code).trim()) miss.push("mb_code");
      if (!categoryData.pantone || !String(categoryData.pantone).trim()) miss.push("pantone");
      return miss;
    }
    if (target === "PWD") {
      // PWD adapter requires a pantone to mint/link the colourant.
      const miss = [];
      if (!categoryData.pantone && !categoryData.pantone_code) miss.push("pantone");
      else {
        const p = categoryData.pantone || categoryData.pantone_code;
        if (!String(p).trim()) miss.push("pantone");
      }
      return miss;
    }
    if (target === "REGRIND") {
      const miss = [];
      if (!categoryData.polymer_id || !String(categoryData.polymer_id).trim()) miss.push("polymer_id");
      if (!categoryData.colour || !String(categoryData.colour).trim()) miss.push("colour");
      return miss;
    }
    return [];
  }, [target, categoryData, topFields, convertAsL2]);

  const canConfirm = target && missingLayer1.length === 0 && missingCategorySpecific.length === 0 && !submitting;

  const submit = async () => {
    setSubmitting(true);
    setServerError(null);
    try {
      const body = {
        target_category: target,
        category_data: categoryData,
        top_fields: { ...topFields, ...(target === "INM" && convertAsL2 ? { rm_level: "L2" } : {}) },
      };
      const res = await axios.post(
        `${API}/raw-materials/${rm.rm_id}/convert-category`,
        body,
      );
      toast.success(
        `Converted ${rm.rm_id}: ${rm.category} → ${target}. Undo batch: ${res.data.batch_id}`,
        { duration: 8000 },
      );
      onSuccess?.(res.data);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        setServerError(detail);
      } else {
        setServerError({ message: detail || err.message || "Conversion failed" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!rm) return null;

  const availableTargets = Object.entries(rmCategories || {})
    .filter(([code]) => code !== rm.category)
    .sort(([a], [b]) => a.localeCompare(b));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-lg max-h-[92vh] overflow-auto"
        data-testid="convert-category-dialog"
      >
        <DialogHeader>
          <DialogTitle>Convert Category</DialogTitle>
          <DialogDescription>
            Move <code className="px-1 rounded bg-muted">{rm.rm_id}</code> from
            <span className="font-medium"> {rm.category}</span> to a new category.
            The target&apos;s create-time enrichment (colourant resolve/mint, BOM
            derive, autolink) runs — so the converted row is
            indistinguishable from a natively-created one. Reversible via
            the returned <code>batch_id</code>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Target Category *</Label>
            <Select
              value={target || "select"}
              onValueChange={(v) => {
                const nv = v === "select" ? "" : v;
                setTarget(nv);
                setCategoryData({});
                setTopFields({});
                setSelectedMould(null);
                setConvertAsL2(false);
                setServerError(null);
              }}
            >
              <SelectTrigger data-testid="convert-target-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {availableTargets.map(([code, cat]) => (
                  <SelectItem key={code} value={code}>
                    {code} — {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {targetCfg && (
              <p className="text-xs text-muted-foreground mt-1">
                Source type on conversion: <code>{targetCfg.default_source_type || "PURCHASED"}</code>
              </p>
            )}
          </div>

          {/* INP — mould + pantone */}
          {target === "INP" && (
            <div className="border-2 border-blue-200 bg-blue-50 rounded-lg p-3 space-y-3">
              <p className="text-xs font-medium text-blue-900">INP conversion (mould-driven)</p>
              <MouldSearchSelect
                testIdPrefix="convert-inp-mould"
                label="Mould Code"
                required
                value={selectedMould}
                onSelect={(m) => {
                  setSelectedMould(m);
                  setTopFields({ ...topFields, mould_id: m.id });
                }}
                onClear={() => {
                  setSelectedMould(null);
                  const { mould_id: _drop, ...rest } = topFields;
                  setTopFields(rest);
                }}
              />
              <div>
                <Label>Pantone Code *</Label>
                <Input
                  value={categoryData.pantone_code || ""}
                  onChange={(e) => setCategoryData({ ...categoryData, pantone_code: e.target.value })}
                  placeholder="e.g. 427 C"
                  data-testid="convert-inp-pantone"
                />
              </div>
              <p className="text-[11px] text-blue-800">
                Backend resolves the INJECTION colourant on the mould&apos;s polymer
                base, mints it if absent, then derives the INP BOM.
              </p>
            </div>
          )}

          {/* INM — pantone (+ optional L2 fields) */}
          {target === "INM" && (
            <div className="border-2 border-amber-200 bg-amber-50 rounded-lg p-3 space-y-3">
              <p className="text-xs font-medium text-amber-900">INM conversion (pantone-driven)</p>
              <div>
                <Label>Pantone Code *</Label>
                <Input
                  value={categoryData.pantone_code || ""}
                  onChange={(e) => setCategoryData({ ...categoryData, pantone_code: e.target.value })}
                  placeholder="e.g. 427 C"
                  data-testid="convert-inm-pantone"
                />
              </div>
              <div>
                <Label>PWD RM ID (optional)</Label>
                <Input
                  value={topFields.pwd_rm_id || ""}
                  onChange={(e) => setTopFields({ ...topFields, pwd_rm_id: e.target.value })}
                  placeholder="e.g. PWD_00045"
                  data-testid="convert-inm-pwd-rm-id"
                />
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-amber-200">
                <Checkbox
                  id="convert-inm-as-l2"
                  checked={convertAsL2}
                  onCheckedChange={(v) => setConvertAsL2(!!v)}
                  data-testid="convert-inm-as-l2"
                />
                <Label htmlFor="convert-inm-as-l2" className="cursor-pointer">
                  Convert as L2 (coated part)
                </Label>
              </div>
              {convertAsL2 && (
                <div className="space-y-2 border-t border-amber-200 pt-2">
                  <div>
                    <Label>Parent RM ID * (INM_FAB base part)</Label>
                    <Input
                      value={topFields.parent_rm_id || ""}
                      onChange={(e) => setTopFields({ ...topFields, parent_rm_id: e.target.value })}
                      data-testid="convert-inm-parent-rm-id"
                    />
                  </div>
                  <div>
                    <Label>Secondary L1 RM ID * (PWD)</Label>
                    <Input
                      value={topFields.secondary_l1_rm_id || ""}
                      onChange={(e) => setTopFields({ ...topFields, secondary_l1_rm_id: e.target.value })}
                      data-testid="convert-inm-secondary-l1"
                    />
                  </div>
                  <div>
                    <Label>Powder Qty (grams) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={topFields.powder_qty_grams || ""}
                      onChange={(e) => setTopFields({ ...topFields, powder_qty_grams: e.target.value })}
                      data-testid="convert-inm-powder-qty"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Coating scrap factor</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={topFields.coating_scrap_factor ?? ""}
                        onChange={(e) => setTopFields({ ...topFields, coating_scrap_factor: e.target.value })}
                        placeholder="0.10 (default)"
                      />
                    </div>
                    <div>
                      <Label>Processing cost</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={topFields.processing_cost ?? ""}
                        onChange={(e) => setTopFields({ ...topFields, processing_cost: e.target.value })}
                        placeholder="0 (default)"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* REGRIND — polymer_id + colour */}
          {target === "REGRIND" && (
            <div className="border-2 border-emerald-200 bg-emerald-50 rounded-lg p-3 space-y-3">
              <p className="text-xs font-medium text-emerald-900">REGRIND conversion</p>
              <div>
                <Label>Polymer ID * (existing POLY rm_id)</Label>
                <Input
                  value={categoryData.polymer_id || ""}
                  onChange={(e) => setCategoryData({ ...categoryData, polymer_id: e.target.value })}
                  placeholder="POLY_00042"
                  data-testid="convert-regrind-polymer-id"
                />
              </div>
              <div>
                <Label>Colour *</Label>
                <Input
                  value={categoryData.colour || ""}
                  onChange={(e) => setCategoryData({ ...categoryData, colour: e.target.value })}
                  placeholder="e.g. Black"
                  data-testid="convert-regrind-colour"
                />
              </div>
              <p className="text-[11px] text-emerald-800">
                Polymer grade auto-derived from the linked POLY.
              </p>
            </div>
          )}

          {/* Generic / MB / PWD / INM_FAB / everything else — loop
              rmCategories[target].fields. For INP / INM we already have
              dedicated blocks above; skip the loop for them. */}
          {target && target !== "INP" && target !== "INM" && target !== "REGRIND" && (
            <div className="space-y-3">
              {targetFields.length > 0 && (
                <div className="p-3 bg-gray-50 border rounded-lg">
                  <p className="text-sm font-medium mb-2">Fields for {target}:</p>
                  <div className="flex flex-wrap gap-2">
                    {targetFields.map(f => {
                      const isRequired = requiredKeys.includes(f);
                      return (
                        <span
                          key={f}
                          className={`text-xs px-2 py-1 rounded ${isRequired ? "bg-red-100 text-red-800" : "bg-gray-200"}`}
                        >
                          {formatLabel(f)}{isRequired ? " *" : ""}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              {targetFields.map(field => (
                <div key={field}>
                  <Label>
                    {formatLabel(field)}{requiredKeys.includes(field) ? " *" : ""}
                  </Label>
                  <Input
                    placeholder={`Enter ${formatLabel(field).toLowerCase()}`}
                    value={categoryData[field] || ""}
                    onChange={(e) => setCategoryData({ ...categoryData, [field]: e.target.value })}
                    data-testid={`convert-generic-${field}`}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Inline server error — Layer-1 400s include a `missing[]` list. */}
          {serverError && (
            <div className="border border-red-300 bg-red-50 rounded-lg p-3 text-sm" data-testid="convert-error-panel">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-900" data-testid="convert-server-error">
                    {friendlyErrorTitle(serverError)}
                  </p>
                  {friendlyErrorHint(serverError) && (
                    <p className="text-xs text-red-800 mt-1">
                      {friendlyErrorHint(serverError)}
                    </p>
                  )}
                  {serverError.code && (
                    <p className="text-[11px] text-red-700 mt-1 opacity-70">
                      Code: <code>{serverError.code}</code>
                    </p>
                  )}
                  {Array.isArray(serverError.missing) && serverError.missing.length > 0 && (
                    <div className="mt-1 text-xs text-red-800">
                      Missing: <code>{serverError.missing.join(", ")}</code>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={!canConfirm}
              data-testid="convert-confirm-btn"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Convert to {target || "…"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
